import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components/macro";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import {
  CardContent as MuiCardContent,
  Grid,
  Link,
  Button as MuiButton,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Typography as MuiTypography,
  Alert as MuiAlert,
  Paper as MuiPaper,
  IconButton,
  CardActions,
  DialogContent,
} from "@mui/material";
import { spacing, display } from "@mui/system";
import HomeIcon from "@mui/icons-material/Home";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import { CircularIndeterminate } from "../components/Progress";
import axios from "../../utils/axios";
import {
  COLOR_MATCH_PLAYER_STATUSES,
  COLOR_MATCH_STATUSES,
} from "../../constants";
import convertUSD from "../../utils/convertUSD";
import Dialog from "../../components/Dialog";
import { rgba } from "polished";
import convertUTC from "../../utils/convertUTC";
import { SimpleDataGrid } from "../../components/CustomDataGrid";
import StatusChip from "../../components/StatusChip";
import {
  CashCell,
  CountryCell,
  IsWinnerCell,
} from "../../components/CustomCells";
import {
  CheckboxGroupField,
  MultilineTextField,
  RadioGroupField,
  SingleSelect,
} from "../../utils/form";
import useConfirm from "../../components/useConfirm";

const Card = styled(MuiCard)`
  ${spacing};

  box-shadow: none;
`;

const CardContent = styled(MuiCardContent)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Paper = styled(MuiPaper)(spacing);

const LogsPaper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(2, 4)};
  background-color: ${(props) => rgba(props.theme.palette.primary.main, 0.125)};
  color: ${(props) => props.theme.palette.primary.main};
`;

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Button = styled(MuiButton)(spacing);

const Typography = styled(MuiTypography)(display);

const Alert = styled(MuiAlert)(spacing);

const Logs = ({ logs, id }) => {
  const [dialog, setDialog] = useState(false);

  const handleOpenDialog = () => setDialog(true);
  const handleCloseDialog = () => setDialog(false);

  const downloadLogs = useCallback(() => {
    const element = document.createElement("a");
    const prettyText = logs.join(`\n`);
    console.log(prettyText);
    const file = new Blob([prettyText], {
      type: "text/plain",
    });
    element.href = URL.createObjectURL(file);
    element.download = `${id}.txt`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
    element.remove();
  }, [id, logs]);

  return (
    <>
      <Grid container spacing={2} direction="row">
        <Grid item>
          <IconButton
            color="primary"
            aria-label="View Logs"
            onClick={(event) => {
              event.stopPropagation();
              handleOpenDialog();
            }}
          >
            <VisibilityOutlinedIcon />
          </IconButton>
        </Grid>
        <Grid item>
          <IconButton
            color="primary"
            aria-label="Download Logs"
            onClick={(event) => {
              event.stopPropagation();
              downloadLogs();
            }}
          >
            <DownloadOutlinedIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Dialog open={dialog} title="Logs" onClose={handleCloseDialog}>
        <Card elevation={0}>
          <CardContent>
            <LogsPaper>
              {logs.map(
                (line) =>
                  line !== "" && (
                    <Typography
                      variant="body1"
                      style={{ wordBreak: "break-all" }}
                      key={line}
                    >
                      {line}
                    </Typography>
                  )
              )}
            </LogsPaper>
          </CardContent>
        </Card>
      </Dialog>
    </>
  );
};

const columns = [
  {
    accessorKey: "id",
    header: "ID",
  },
  {
    accessorKey: "name",
    header: "Name",
  },
  {
    accessorKey: "countryId",
    header: "Country",
    Cell: CountryCell,
  },
  {
    accessorKey: "status",
    header: "Status",
    filterVariant: "multi-select",
    Cell: ({ cell }) => (
      <StatusChip
        options={COLOR_MATCH_PLAYER_STATUSES}
        value={cell.getValue()}
        label={cell?.column?.columnDef?.selectOptionsLookup?.[cell.getValue()]}
        size="small"
        color="success"
      />
    ),
  },
  {
    accessorKey: "isWinner",
    header: "Winner",
    Cell: IsWinnerCell,
  },
  {
    accessorKey: "order",
    header: "Order",
  },
  {
    accessorKey: "score",
    header: "Score",
  },
  {
    accessorKey: "winCash",
    header: "Cash Prize",
    Cell: CashCell,
  },
  {
    accessorKey: "winChips",
    header: "Chips Prize",
  },
  {
    accessorKey: "video",
    header: "Video",
    Cell: ({ cell }) => (
      <IconButton
        onClick={(event) => {
          event.stopPropagation();
          window.open(cell.getValue(), "_blank");
        }}
        disabled={!cell.getValue()}
      >
        <PlayCircleFilledWhiteOutlinedIcon />
      </IconButton>
    ),
  },
  {
    accessorKey: "logs",
    filterVariant: "text",
    header: "Logs",
    Cell: ({ cell, row }) => (
      <Logs logs={cell.getValue()} id={row.original.id} />
    ),
  },
];

function DataGridUserMatches({ loading, options, data }) {
  const navigate = useNavigate();

  return (
    <SimpleDataGrid
      columns={columns}
      options={options}
      data={data}
      muiTableBodyRowProps={({ row }) => ({
        onClick: () => navigate(`/users/${row.original.id}`),
        sx: {
          cursor: "pointer",
        },
      })}
      loading={loading}
      autoHeight
    />
  );
}

const MatchInfo = ({ title, value }) => (
  <>
    <Typography variant="caption">{title}</Typography>
    <Typography variant="body2" component="div">
      {value}
    </Typography>
  </>
);

function MatchView() {
  const { matchId } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [options, setOptions] = useState({});
  const [playersOptions, setPlayersOptions] = useState({});
  const [cancelOptions, setCancelOptions] = useState({});
  const [match, setMatch] = useState({
    type: 0,
    startDate: 0,
    endDate: 0,
    prize: 0,
    playersAmount: 0,
    isCash: false,
    entryFee: 0,
    id: "",
    title: "",
    players: [],
    logs: [],
    isFinished: false,
  });
  const [players, setPlayers] = useState([]);
  const [ConfirmDialog, confirmAction] = useConfirm(
    "Are you sure you want to update match?"
  );

  const getMatch = useCallback(async () => {
    try {
      setLoading(true);

      const { data: options } = await axios.options(
        `/api/v1/matches/${matchId}`
      );
      setOptions(options);

      const { data: match } = await axios.get(`/api/v1/matches/${matchId}`);
      setMatch(match);

      const { data: playersOptions } = await axios.options(
        `/api/v1/matches/${matchId}/players`
      );
      setPlayersOptions(playersOptions);

      const { data: players } = await axios.get(
        `/api/v1/matches/${matchId}/players`
      );
      setPlayers(players);

      const { data: cancelOptions } = await axios.options(
        `/api/v1/matches/${matchId}/cancel`
      );
      setCancelOptions(cancelOptions);
    } catch (e) {
      setError(e.message);
      console.log(e);
    }
    setLoading(false);
  }, [setLoading, setError, setMatch, matchId]);

  const updateMatch = useCallback(
    async (
      { creditIds, ...values },
      { setErrors, setStatus, setSubmitting, resetForm }
    ) => {
      const isConfirmed = await confirmAction();

      if (!isConfirmed) {
        return;
      }
      try {
        setLoading(true);
        const { data } = await axios.post(`/api/v1/matches/${matchId}`, values);

        creditIds.forEach(
          async (playerId) =>
            await axios.post(`/api/v1/matches/${matchId}/credit/${playerId}`)
        );

        const { data: players } = await axios.get(
          `/api/v1/matches/${matchId}/players`
        );
        setPlayers(players);

        resetForm();

        setMatch(data);

        setEdit(false);
      } catch (error) {
        console.log(error);
        const message = error.messages || "Something went wrong";

        setStatus({ success: false });
        setErrors({ submit: message });
        setSubmitting(false);
      }
      setLoading(false);
    },
    [setLoading, setError, setMatch, matchId]
  );

  const cancelMatch = useCallback(async () => {
    try {
      setLoading(true);

      const { data: match } = await axios.post(
        `/api/v1/matches/${matchId}/cancel`
      );
      setMatch(match);
    } catch (e) {
      setError(e.message);
      console.log(e);
    }
    setLoading(false);
  }, [setLoading, setError, setMatch, matchId]);

  useEffect(() => {
    getMatch();
  }, [getMatch]);

  if (loading) {
    return <CircularIndeterminate />;
  }

  if (error) {
    return (
      <Alert mb={4} severity="error">
        {error}
      </Alert>
    );
  }

  return (
    <React.Fragment>
      <Helmet title={`Match ${matchId}`} />

      <Typography variant="h3" gutterBottom display="inline">
        Match {matchId}
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          <HomeIcon />
        </Link>
        <Link component={NavLink} to="/matches">
          Matches
        </Link>
        <Typography>{matchId}</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Card>
        <Formik
          initialValues={{
            winnerId: players[0]?.id,
            creditIds: [],
            reason: options.reason?.[0]?.value,
            reasonText: "",
          }}
          enableReinitialize
          validationSchema={Yup.object().shape({
            winnerId: Yup.string().required("Winner is required"),
            creditIds: Yup.array(),
            reason: Yup.string().required("Reason is required"),
            reasonText: Yup.string().required("Reason text is required"),
          })}
          onSubmit={updateMatch}
        >
          {({ handleSubmit, isSubmitting, values, resetForm }) => (
            <form noValidate onSubmit={handleSubmit}>
              <CardContent pb={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Grid
                      container
                      spacing={{ xs: 8, md: 12, lg: 20 }}
                      direction="row"
                    >
                      <Grid item xs="auto">
                        <Grid container spacing={4} direction="column">
                          <Grid item xs={12}>
                            <MatchInfo title="Match ID" value={match.id} />
                          </Grid>
                          <Grid item xs={12}>
                            <MatchInfo title="Match Name" value={match.title} />
                          </Grid>
                          <Grid item xs={12}>
                            <MatchInfo
                              title="Players Amount"
                              value={match.players?.length}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <MatchInfo
                              title="Match Status"
                              value={
                                <StatusChip
                                  options={COLOR_MATCH_STATUSES}
                                  value={match.statusMatch}
                                  label={
                                    options?.statusMatch?.find(
                                      (entry) =>
                                        entry.value === match.statusMatch
                                    )?.display_name
                                  }
                                />
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <MatchInfo
                              title="Match Category Name"
                              value={match.category || "-"}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs="auto">
                        <Grid container spacing={4} direction="column">
                          <Grid item xs={12}>
                            <MatchInfo
                              title="Entry Fee"
                              value={
                                match.isCash
                                  ? convertUSD(match.entryFee)
                                  : `${match.entryFee} chips`
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <MatchInfo
                              title="Prize"
                              value={
                                match.isCash
                                  ? convertUSD(match.prize)
                                  : `${match.prize} chips`
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <MatchInfo
                              title="Match Start Date"
                              value={convertUTC(match.startDate)}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <MatchInfo
                              title="Match End Date"
                              value={
                                match.endDate
                                  ? convertUTC(match.endDate)
                                  : "Not Finished"
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      {edit && (
                        <Grid item xs="auto">
                          <Grid container spacing={4} direction="column">
                            <Grid item xs={12}>
                              <Grid
                                container
                                spacing={4}
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Grid item xs="auto">
                                  <RadioGroupField
                                    name="winnerId"
                                    label="Choose the Winner"
                                    options={players.map((player) => ({
                                      value: player.id,
                                      label: player.name,
                                    }))}
                                  />
                                </Grid>
                                <Grid item xs="auto">
                                  <CheckboxGroupField
                                    name="creditIds"
                                    label="Credit the User"
                                    options={players.map((player) => ({
                                      value: player.id,
                                      label: player.name,
                                    }))}
                                    values={values}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={12}>
                              <Field
                                name="reason"
                                label="Select the Reason for Edit"
                                component={SingleSelect}
                                options={options.reason}
                                onChange={(event, setFieldValue) => {
                                  setFieldValue(
                                    "reason",
                                    options.reason.find(
                                      (reason) =>
                                        reason.value === event.target.value
                                    ).value
                                  );
                                  setFieldValue("reasonText", "");
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Field
                                name="reasonText"
                                label="Specify the Reason"
                                component={MultilineTextField}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
              {edit && (
                <CardActions>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    Confirm
                  </Button>
                  <Button
                    color="primary"
                    disabled={isSubmitting}
                    onClick={() => {
                      resetForm();
                      setEdit(false);
                    }}
                  >
                    Cancel
                  </Button>
                </CardActions>
              )}
            </form>
          )}
        </Formik>
        {!edit && (
          <CardActions>
            <Button
              color="primary"
              variant="contained"
              onClick={() => setEdit(true)}
            >
              Edit the Match
            </Button>
            <Button color="primary" onClick={() => setOpenDialog(true)}>
              Cancel the Match
            </Button>
          </CardActions>
        )}
        {!edit && (
          <DataGridUserMatches
            loading={loading}
            options={playersOptions}
            data={players}
          />
        )}
      </Card>
      <Dialog
        open={openDialog}
        title="Cancel the Match"
        onClose={() => setOpenDialog(false)}
      >
        <DialogContent>
          <Formik
            initialValues={{
              reason: options.reason?.[0]?.value,
              reasonText: "",
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              reason: Yup.string().required("Reason is required"),
              reasonText: Yup.string().required("Reason text is required"),
            })}
            onSubmit={cancelMatch}
          >
            {({ handleSubmit, isSubmitting, values, resetForm }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Grid container spacing={6}>
                  <Grid item xs={12}>
                    <Grid container spacing={4}>
                      <Grid item xs={12}>
                        <Typography>
                          Please, specify the reason for the match cancellation
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          name="reason"
                          label="Reason for Cancel"
                          component={SingleSelect}
                          options={options.reason}
                          onChange={(event, setFieldValue) => {
                            setFieldValue(
                              "reason",
                              options.reason.find(
                                (reason) => reason.value === event.target.value
                              ).value
                            );
                            setFieldValue("reasonText", "");
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          name="reasonText"
                          label="Specify the reason"
                          component={MultilineTextField}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={4}>
                      <Grid item xs="auto">
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={isSubmitting}
                        >
                          Confirm
                        </Button>
                      </Grid>
                      <Grid item xs="auto">
                        <Button
                          color="primary"
                          disabled={isSubmitting}
                          onClick={() => {
                            resetForm();
                            setOpenDialog(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </React.Fragment>
  );
}

export default MatchView;
